import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect } from 'react';

import {
  fetchProductComparisonTable,
  getProductRelationsForComparableProductIds,
  resetProductComparisonTable
} from 'actions/productdetail/productComparison';
import { ProductComparisonAPIResponse, ProductFeature, ProductFeatureValue, ProductHeader } from 'types/productComparison';
import ColorSwatchWrapper from 'components/search/ColorSwatches';
import { formatHeartsForSlots } from 'helpers/HeartUtils';
import useEffectOnce from 'hooks/useEffectOnce';
import { Loader } from 'components/Loader';
import UtilityStrokeCheckOutlineSmallIcon from 'tailwind/components/Icons/UtilityStrokeCheckOutlineSmallIcon';
import UtilityStrokeCheckOutlineMediumIcon from 'tailwind/components/Icons/UtilityStrokeCheckOutlineMediumIcon';
import UtilityStrokeCloseOutlineMediumIcon from 'tailwind/components/Icons/UtilityStrokeCloseOutlineMediumIcon';
import UtilityStrokeCloseOutlineSmallIcon from 'tailwind/components/Icons/UtilityStrokeCloseOutlineSmallIcon';
import useWindowSize from 'hooks/useWindowSize';
import useMartyContext from 'hooks/useMartyContext';
import { track } from 'apis/amethyst';
import { evProductComparisonTableView, evProductInteract } from 'events/productComparison';
import { PRODUCT_INTERACTION_TYPE } from 'constants/amethystEnums';
import { lowercaseThenCapitalize } from 'helpers/index';

import css from 'styles/components/productdetail/productComparisonTable.scss';

interface OwnProps {
  styleId: string;
  productId: string;
}

const ProductComparisonTable = (props: OwnProps) => {
  const { styleId, productId } = props;
  const dispatch = useDispatch();
  const {
    comparisonTableData: {
      comparisonModule: { header: headerProducts = [], row: productFeatures }
    },
    comparisonTableDataStatus
  } = useSelector(state => state.productComparison);

  const { width: windowWidth = 0, height: windowHeight = 0 } = useWindowSize();
  const isMobile = windowWidth < 650 || windowHeight < 650;
  const { testId } = useMartyContext();

  useEffectOnce(() => {
    dispatch(fetchProductComparisonTable(styleId));

    return () => {
      dispatch(resetProductComparisonTable());
    };
  });

  useEffect(() => {
    if (headerProducts.length > 0) {
      const productIds = headerProducts.map(headerProduct => headerProduct.productId);
      dispatch(getProductRelationsForComparableProductIds(productIds));
      track(() => [evProductComparisonTableView, { sourceProductId: productId, productIds }]);
    }
  }, [headerProducts.length]);

  if (comparisonTableDataStatus === ProductComparisonAPIResponse.FAILED || comparisonTableDataStatus === ProductComparisonAPIResponse.UNKNOWN) {
    return null;
  }
  if (comparisonTableDataStatus === ProductComparisonAPIResponse.FETCHING) {
    return <Loader />;
  }

  const makeAttributeValue = (values: ProductFeatureValue, header: ProductHeader) => {
    const val = values[header.linkedAsin] || '';
    switch (val) {
      case '✔':
      case 'APLUS-TRUE':
        return isMobile ? <UtilityStrokeCheckOutlineSmallIcon size={16} /> : <UtilityStrokeCheckOutlineMediumIcon size={32} />;
      case '✘':
      case 'APLUS-FALSE':
        return isMobile ? <UtilityStrokeCloseOutlineSmallIcon size={16} /> : <UtilityStrokeCloseOutlineMediumIcon size={32} />;
      default:
        return val;
    }
  };

  const createProductCardWithColorSwatches = (currentProduct: ProductHeader) => {
    const { productWithRelations: productWithRelatedStyles } = currentProduct;
    if (!productWithRelatedStyles) {
      return null;
    }
    const { relatedStyles, badges, ...productInfo } = productWithRelatedStyles;

    if (!productInfo || !productInfo.productUrl) {
      return null;
    }

    const onProductClicked = () => {
      track(() => [
        evProductInteract,
        {
          sourceStyleId: styleId,
          interactedProduct: productInfo,
          interactionType: PRODUCT_INTERACTION_TYPE.Click
        }
      ]);
    };
    const onProductMediaHovered = () => {
      track(() => [
        evProductInteract,
        {
          sourceStyleId: styleId,
          interactedProduct: productInfo,
          interactionTyepe: PRODUCT_INTERACTION_TYPE.Hover
        }
      ]);
    };

    const heartsData = {
      showFavoriteHeart: false,
      hearts: [],
      heartsList: {}
    };

    const heartsInfo = formatHeartsForSlots(productInfo, heartsData);
    return (
      <ColorSwatchWrapper
        {...productInfo}
        relatedStyles={relatedStyles}
        onClick={onProductClicked}
        onProductMediaHovered={onProductMediaHovered}
        testId={testId('comparableProducts')}
        badges={badges}
        heartsInfo={heartsInfo}
        className={css.headerProductImage}
      />
    );
  };

  return (
    <div className={css.comparisonTableComponent}>
      <table className={css.dynamicTable}>
        <caption className={css.tableHeader}>How they compare</caption>
        <thead>
          <tr>
            {headerProducts.map((product: ProductHeader) => (
              <th scope="col" key={product.linkedAsin}>
                <div className={css.headerProduct}>{createProductCardWithColorSwatches(product)}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {productFeatures.map((property: ProductFeature) => (
            <React.Fragment key={`${property.label}_row`}>
              <tr key={`${property.label}_tr`}>
                <td key={`${property.label}`} className={css.attributeLabel}>
                  {lowercaseThenCapitalize(property.label)}
                </td>
              </tr>
              <tr className={css.attributeValueRow}>
                {headerProducts.map(h => (
                  <td key={`${h.linkedAsin}_val`} className={css.attributeValue}>
                    {makeAttributeValue(property.value, h)}
                  </td>
                ))}
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProductComparisonTable;
